import { useEffect, useState } from 'react';
import { Card, DataTable, StatusBadge, Modal, FormField, FormSelect, TextInput, Toggle, ConfirmDialog, EmptyState, PageHeader } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { labelFor } from '@/shared/lib/labels';
import { superApi } from '@/shared/api/superApi';
import { toast } from '@/components/ui/Toast';

interface PlatformUser {
  id: number;
  name: string;
  email: string;
  role: string;
  is_super_admin: boolean;
  two_factor_enabled: boolean;
  status: string;
  last_login_at?: string;
  tenant_id?: number | null;
  tenant_name?: string | null;
}

interface TenantOpt { id: number; name: string }

export function InternalUsers() {
  const [rows, setRows] = useState<PlatformUser[]>([]);
  const [loading, setLoading] = useState(true);
  // On a permissions screen, an outage must NOT look like "zero super-admins
  // exist" — track the error and show retry instead of coercing to [].
  const [loadError, setLoadError] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<PlatformUser | null>(null);
  const [confirmSuspend, setConfirmSuspend] = useState<PlatformUser | null>(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', is_super_admin: true, role: 'admin', tenant_id: '' });
  // רשימת הלקוחות (מרכזיות) לבורר — משתמש שאינו סופר-מלא חייב שיוך ללקוח.
  const [tenants, setTenants] = useState<TenantOpt[]>([]);

  useEffect(() => {
    void load();
    void superApi.tenants({ per_page: 500 }).then((r) => setTenants((r.data as TenantOpt[]) ?? [])).catch(() => {});
  }, []);

  async function load() {
    setLoading(true);
    try {
      setRows((await superApi.internalUsers()) as PlatformUser[]);
      setLoadError(false);
    } catch {
      setLoadError(true); // keep last rows; never fabricate an empty list
    } finally {
      setLoading(false);
    }
  }

  async function create() {
    if (!form.name || !form.email || !form.password) {
      toast('יש למלא שם, אימייל וסיסמה', 'warn');
      return;
    }
    if (!form.is_super_admin && !form.tenant_id) {
      toast('משתמש שאינו Super Admin מלא חייב להיות משויך ללקוח — בחר מרכזיה', 'warn');
      return;
    }
    try {
      await superApi.createInternalUser({
        ...form,
        tenant_id: form.is_super_admin ? null : Number(form.tenant_id),
      });
      toast('המשתמש נוצר', 'ok');
      setShowAdd(false);
      setForm({ name: '', email: '', password: '', is_super_admin: true, role: 'admin', tenant_id: '' });
      void load();
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } }).response?.data?.message;
      toast(msg ?? 'שגיאה ביצירה (אימייל כבר קיים?)', 'danger');
    }
  }

  async function saveEdit() {
    if (!editing) return;
    if (!editing.is_super_admin && !editing.tenant_id) {
      toast('משתמש שאינו Super Admin מלא חייב להיות משויך ללקוח — בחר מרכזיה', 'warn');
      return;
    }
    try {
      await superApi.updateInternalUser(editing.id, {
        name: editing.name,
        is_super_admin: editing.is_super_admin,
        role: editing.role,
        status: editing.status,
        tenant_id: editing.is_super_admin ? null : editing.tenant_id,
      });
      toast('נשמר', 'ok');
      setEditing(null);
      void load();
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } }).response?.data?.message;
      toast(msg ?? 'שגיאה בשמירה', 'danger');
    }
  }

  async function suspend(u: PlatformUser) {
    try {
      await superApi.suspendInternalUser(u.id);
      toast('המשתמש הושעה', 'ok');
      void load();
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } }).response?.data?.message;
      toast(msg ?? 'שגיאה בהשעיה', 'danger');
    }
  }

  async function reset2fa(u: PlatformUser) {
    try {
      await superApi.resetInternalUser2fa(u.id);
      toast('2FA אופס', 'ok');
      void load();
    } catch {
      toast('שגיאה באיפוס 2FA', 'danger');
    }
  }

  const cols: DataTableColumn<PlatformUser>[] = [
    { key: 'name', he: 'שם' },
    { key: 'email', he: 'אימייל', render: (r) => <span dir="ltr">{r.email}</span> },
    {
      key: 'is_super_admin',
      he: 'סוג',
      render: (r) =>
        r.is_super_admin
          ? <StatusBadge tone="accent" size="sm">Super</StatusBadge>
          : <StatusBadge tone="neutral" size="sm">{labelFor('role', r.role)}</StatusBadge>,
    },
    {
      key: 'tenant_name',
      he: 'לקוח',
      render: (r) => (r.is_super_admin ? <span style={{ color: 'var(--muted)' }}>כל הלקוחות</span> : (r.tenant_name ?? '—')),
    },
    {
      key: 'two_factor_enabled',
      he: '2FA',
      render: (r) =>
        r.two_factor_enabled
          ? <StatusBadge tone="ok" size="sm">מופעל</StatusBadge>
          : <StatusBadge tone="warn" size="sm">כבוי</StatusBadge>,
    },
    {
      key: 'status',
      he: 'סטטוס',
      render: (r) => (
        <StatusBadge tone={r.status === 'active' ? 'ok' : 'danger'} dot size="sm">
          {labelFor('user_status', r.status)}
        </StatusBadge>
      ),
    },
    {
      key: 'last_login_at',
      he: 'התחברות אחרונה',
      render: (r) => (r.last_login_at ? new Date(r.last_login_at).toLocaleString('he-IL') : '—'),
    },
    {
      key: '_actions',
      he: '',
      render: (r) => (
        <div style={{ display: 'flex', gap: 4 }} onClick={(e) => e.stopPropagation()}>
          <button type="button" className="btn ghost" onClick={() => setEditing({ ...r })} aria-label="עריכת משתמש">
            <Icon name="edit" size={14} />
          </button>
          {r.two_factor_enabled && (
            <button type="button" className="btn ghost" onClick={() => reset2fa(r)} aria-label="אפס 2FA">
              <Icon name="lock" size={14} />
            </button>
          )}
          {r.status === 'active' && (
            <button type="button" className="btn ghost" onClick={() => setConfirmSuspend(r)} aria-label="השעיית משתמש">
              <Icon name="close" size={14} color="var(--danger)" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      <PageHeader title="משתמשי-על" actions={<>
        <button type="button" className="btn primary" onClick={() => setShowAdd(true)}>+ משתמש חדש</button>
      </>} />
      {loadError && rows.length === 0 ? (
        <Card>
          <EmptyState
            icon="warn"
            title="שגיאה בטעינת משתמשי-העל"
            description="לא ניתן היה לטעון את רשימת המשתמשים מהשרת. אל תניח שאין משתמשים — נסה שוב."
            action={<button type="button" className="btn primary" onClick={() => void load()}>נסה שוב</button>}
          />
        </Card>
      ) : (
        <Card padded={false}>
          <DataTable<PlatformUser> columns={cols} rows={rows} loading={loading} />
        </Card>
      )}

      <Modal
        isOpen={showAdd}
        onClose={() => setShowAdd(false)}
        title="משתמש-על חדש"
        footer={
          <>
            <button type="button" className="btn" onClick={() => setShowAdd(false)}>ביטול</button>
            <button type="button" className="btn primary" onClick={create}>צור</button>
          </>
        }
      >
        <div className="stack">
          <FormField label="שם" required><TextInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></FormField>
          <FormField label="אימייל" required><TextInput type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} dir="ltr" /></FormField>
          <FormField label="סיסמה (מינ׳ 8)" required><TextInput type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></FormField>
          <Toggle checked={form.is_super_admin} onChange={(v) => setForm({ ...form, is_super_admin: v })} label="הרשאת Super Admin מלאה" />
          {!form.is_super_admin && (
            <>
              <FormField label="לקוח (מרכזיה)" required hint="משתמש שאינו Super Admin מלא עובד בתוך מרכזיה של לקוח מסוים">
                <FormSelect value={form.tenant_id} onChange={(e) => setForm({ ...form, tenant_id: e.target.value })}>
                  <option value="">בחר לקוח…</option>
                  {tenants.map((t) => (
                    <option key={t.id} value={String(t.id)}>{t.name}</option>
                  ))}
                </FormSelect>
              </FormField>
              <FormField label="תפקיד">
                <FormSelect value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                  <option value="admin">מנהל</option>
                  <option value="supervisor">מפקח</option>
                  <option value="viewer">צופה</option>
                </FormSelect>
              </FormField>
            </>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title={`עריכת ${editing?.name ?? ''}`}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setEditing(null)}>ביטול</button>
            <button type="button" className="btn primary" onClick={saveEdit}>שמור</button>
          </>
        }
      >
        {editing && (
          <div className="stack">
            <FormField label="שם"><TextInput value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></FormField>
            <FormField label="אימייל"><TextInput value={editing.email} disabled dir="ltr" /></FormField>
            <Toggle checked={editing.is_super_admin} onChange={(v) => setEditing({ ...editing, is_super_admin: v })} label="Super Admin" />
            {!editing.is_super_admin && (
              <FormField label="לקוח (מרכזיה)" required>
                <FormSelect
                  value={editing.tenant_id ? String(editing.tenant_id) : ''}
                  onChange={(e) => setEditing({ ...editing, tenant_id: e.target.value ? Number(e.target.value) : null })}
                >
                  <option value="">בחר לקוח…</option>
                  {tenants.map((t) => (
                    <option key={t.id} value={String(t.id)}>{t.name}</option>
                  ))}
                </FormSelect>
              </FormField>
            )}
            <FormField label="סטטוס">
              <FormSelect value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                <option value="active">פעיל</option>
                <option value="suspended">מושעה</option>
              </FormSelect>
            </FormField>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={!!confirmSuspend}
        onClose={() => setConfirmSuspend(null)}
        onConfirm={() => confirmSuspend && suspend(confirmSuspend)}
        title="השעיית משתמש"
        message={`האם להשעות את ${confirmSuspend?.name}? המשתמש לא יוכל להתחבר.`}
        confirmLabel="השעה"
        tone="danger"
      />
    </div>
  );
}
